import axios from 'axios';
import { API_BASE_URL } from '@/config/serverConfig';

import errorHandler from './errorHandler';
import successHandler from './successHandler';
import CookieManager from '@/utils/helpers/cookieUtils';
import { TOKEN_COOKIE_STORAGE } from '@/constants/localStorageKeyConstants';
import cryptoHelper from '@/utils/crypto';
import getCurrentTime from '@/utils/helpers/getCurrentTime';
import signatureGenerator from '@/utils/hashSignature';

axios.defaults.baseURL = API_BASE_URL;
axios.defaults.withCredentials = true;

let token = cryptoHelper.decrypt(CookieManager.getCookie(TOKEN_COOKIE_STORAGE));
if (token) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
}

async function setToken() {
  const current = getCurrentTime();
  const signature = signatureGenerator.CreateSignature(current);

  let token = cryptoHelper.decrypt(CookieManager.getCookie(TOKEN_COOKIE_STORAGE));
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token} ${signature} ${current}`;
  }
}

const request = {
  create: async ({ entity, jsonData }) => {
    try {
      await setToken();
      const response = await axios.post(entity + '/create', jsonData);
      successHandler(response, {
        notifyOnSuccess: true,
        notifyOnFailed: true,
      });
      return response.data;
    } catch (error) {
      return errorHandler(error);
    }
  },
  read: async ({ entity, id }) => {
    try {
      await setToken();
      const response = await axios.get(entity + '/read/' + id);
      successHandler(response, {
        notifyOnSuccess: false,
        notifyOnFailed: true,
      });
      return response.data;
    } catch (error) {
      return errorHandler(error);
    }
  },
  update: async ({ entity, id, jsonData }) => {
    try {
      await setToken();
      const response = await axios.patch(entity + '/update/' + id, jsonData);
      successHandler(response, {
        notifyOnSuccess: true,
        notifyOnFailed: true,
      });
      return response.data;
    } catch (error) {
      return errorHandler(error);
    }
  },

  delete: async ({ entity, id, options = {} }) => {
    try {
      await setToken();
      const response = await axios.delete(entity + '/delete/' + id);
      successHandler(response, {
        notifyOnSuccess: true,
        notifyOnFailed: true,
      });
      return response.data;
    } catch (error) {
      return errorHandler(error);
    }
  },

  filter: async ({ entity, options = {} }) => {
    try {
      await setToken();
      let filter = options.filter ? 'filter=' + options.filter : '';
      let equal = options.equal ? '&equal=' + options.equal : '';
      let query = `?${filter}${equal}`;

      const response = await axios.get(entity + '/filter' + query);
      successHandler(response, {
        notifyOnSuccess: false,
        notifyOnFailed: false,
      });
      return response.data;
    } catch (error) {
      return errorHandler(error);
    }
  },
  // If you still need filterALL, correct its URL construction:
  filterALL: async ({ entity, params = {} }) => {
    try {
      await setToken();
      // Send params in the request body for POST requests
      const response = await axios.post(entity, params);
      return response.data;
    } catch (error) {
      return errorHandler(error);
    }
  },
  search: async ({ entity, options = {} }) => {
    try {
      await setToken();
      let query = '?';
      for (var key in options) {
        query += key + '=' + options[key] + '&';
      }
      query = query.slice(0, -1);

      const response = await axios.get(entity + '/search' + query);

      successHandler(response, {
        notifyOnSuccess: false,
        notifyOnFailed: false,
      });
      return response.data;
    } catch (error) {
      return errorHandler(error);
    }
  },

  list: async ({ entity, options = {} }) => {
    try {
      await setToken();
      let query = '?';
      for (var key in options) {
        query += key + '=' + options[key] + '&';
      }
      query = query.slice(0, -1);

      const response = await axios.get(entity + '/list' + query);

      successHandler(response, {
        notifyOnSuccess: false,
        notifyOnFailed: false,
      });
      return response.data;
    } catch (error) {
      return errorHandler(error);
    }
  },
  listAll: async ({ entity }) => {
    try {
      await setToken();
      const response = await axios.get(entity + '/listAll');

      successHandler(response, {
        notifyOnSuccess: false,
        notifyOnFailed: false,
      });
      return response.data;
    } catch (error) {
      return errorHandler(error);
    }
  },

  post: async ({ entity, jsonData, options = {} }) => {
    try {
      await setToken();
      const response = await axios.post(entity, jsonData);

      return response.data;
    } catch (error) {
      return errorHandler(error);
    }
  },
  // *** MODIFIED GET METHOD TO ACCEPT AND USE PARAMS ***
  get: async ({ entity, params = {} }) => {
    // Add 'params' with a default empty object
    try {
      await setToken();

      // Construct query string from params
      const queryString = Object.keys(params)
        .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
        .join('&');

      const url = queryString ? `${entity}?${queryString}` : entity;

      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      return errorHandler(error);
    }
  },
  getAll: async ({ entity }) => {
    try {
      await setToken();

      const response = await axios.get(entity + '/getAll');
      return response.data;
    } catch (error) {
      return errorHandler(error);
    }
  },
  getPaginated: async ({ entity, params = {} }) => {
    try {
      // Set the Authorization header just before making the request
      await setToken();

      // Construct the query string from the params object
      const queryString = Object.keys(params)
        .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
        .join('&');

      // Build the final URL
      const url = `${entity}${queryString ? `?${queryString}` : ''}`;

      const response = await axios.get(url);
      return response.data; // Return the entire response data object
    } catch (error) {
      return errorHandler(error);
    }
  },
  getAllImpact: async ({ entity, params = {} }) => {
    try {
      // Set the Authorization header just before making the request
      await setToken();

      // Construct the query string from the params object
      const queryString = Object.keys(params)
        .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
        .join('&');

      // Build the final URL
      const url = `${entity}${queryString ? `?${queryString}` : ''}`;

      const response = await axios.get(url);
      return response.data; // Return the entire response data object
    } catch (error) {
      return errorHandler(error);
    }
  },
  getAllMilestone: async ({ entity, params = {} }) => {
    try {
      // Set the Authorization header just before making the request
      await setToken();

      // Construct the query string from the params object
      const queryString = Object.keys(params)
        .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
        .join('&');

      // Build the final URL
      const url = `${entity}${queryString ? `?${queryString}` : ''}`;

      const response = await axios.get(url);
      return response.data; // Return the entire response data object
    } catch (error) {
      return errorHandler(error);
    }
  },
  getAllKPI: async ({ entity, params = {} }) => {
    try {
      // Set the Authorization header just before making the request
      await setToken();

      // Construct the query string from the params object
      const queryString = Object.keys(params)
        .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
        .join('&');

      // Build the final URL
      const url = `${entity}${queryString ? `?${queryString}` : ''}`;

      const response = await axios.get(url);
      return response.data; // Return the entire response data object
    } catch (error) {
      return errorHandler(error);
    }
  },
  searchFilter: async ({ entity, params = {} }) => {
    try {
      // Set the Authorization header just before making the request
      await setToken();

      // Construct the query string from the params object
      const queryString = Object.keys(params)
        .map(key => {
          const value = params[key];
          // Handle array values for query parameters (e.g., ?key=val1&key=val2)
          if (Array.isArray(value)) {
            return value.map(item => `${encodeURIComponent(key)}=${encodeURIComponent(item)}`).join('&');
          }
          return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
        })
        .join('&');

      // Build the final URL
      const url = `${entity}${queryString ? `?${queryString}` : ''}`;

      console.log('Fetching URL:', url); // Log the constructed URL for debugging

      const response = await axios.get(url);
      return response.data; // Return the entire response data object
    } catch (error) {
      return errorHandler(error);
    }
  },
  searchMilestone: async ({ entity, params = {} }) => {
    try {
      // Set the Authorization header just before making the request
      await setToken();

      // Construct the query string from the params object
      const queryString = Object.keys(params)
        .map(key => {
          const value = params[key];
          // Handle array values for query parameters (e.g., ?key=val1&key=val2)
          if (Array.isArray(value)) {
            return value.map(item => `${encodeURIComponent(key)}=${encodeURIComponent(item)}`).join('&');
          }
          return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
        })
        .join('&');

      // Build the final URL
      const url = `${entity}${queryString ? `?${queryString}` : ''}`;

      console.log('Fetching URL:', url); // Log the constructed URL for debugging

      const response = await axios.get(url);
      return response.data; // Return the entire response data object
    } catch (error) {
      return errorHandler(error);
    }
  },
  searchImpact: async ({ entity, params = {} }) => {
    try {
      // Set the Authorization header just before making the request
      await setToken();

      // Construct the query string from the params object
      const queryString = Object.keys(params)
        .map(key => {
          const value = params[key];
          // Handle array values for query parameters (e.g., ?key=val1&key=val2)
          if (Array.isArray(value)) {
            return value.map(item => `${encodeURIComponent(key)}=${encodeURIComponent(item)}`).join('&');
          }
          return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
        })
        .join('&');

      // Build the final URL
      const url = `${entity}${queryString ? `?${queryString}` : ''}`;

      console.log('Fetching URL:', url); // Log the constructed URL for debugging

      const response = await axios.get(url);
      return response.data; // Return the entire response data object
    } catch (error) {
      return errorHandler(error);
    }
  },
  searchKPI: async ({ entity, params = {} }) => {
    try {
      // Set the Authorization header just before making the request
      await setToken();

      // Construct the query string from the params object
      const queryString = Object.keys(params)
        .map(key => {
          const value = params[key];
          // Handle array values for query parameters (e.g., ?key=val1&key=val2)
          if (Array.isArray(value)) {
            return value.map(item => `${encodeURIComponent(key)}=${encodeURIComponent(item)}`).join('&');
          }
          return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
        })
        .join('&');

      // Build the final URL
      const url = `${entity}${queryString ? `?${queryString}` : ''}`;

      console.log('Fetching URL:', url); // Log the constructed URL for debugging

      const response = await axios.get(url);
      return response.data; // Return the entire response data object
    } catch (error) {
      return errorHandler(error);
    }
  },
  patch: async ({ entity, jsonData }) => {
    try {
      await setToken();
      const response = await axios.patch(entity, jsonData);
      successHandler(response, {
        notifyOnSuccess: true,
        notifyOnFailed: true,
      });
      return response.data;
    } catch (error) {
      return errorHandler(error);
    }
  },

  source: () => {
    const CancelToken = axios.CancelToken;
    const source = CancelToken.source();
    return source;
  },
};
export default request;
